import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import numeral from 'numeral';

interface ShiftCustomPlanFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    shift_plans: any[];
    form: any;
    schema: any;
    storages: any[];
    targetForDelete: any[];
    lineId: any;

    meta: any;
}

const state: ShiftCustomPlanFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    shift_plans: [],
    storages: [],
    targetForDelete: [],
    form: null,
    schema: null,
    lineId: null,

    meta: {},
};

const initState: ShiftCustomPlanFormState = cloneDeep(state);

export const SHIFT_VDP_DATA = 'GET_SHIFT_VDP_DATA';
export const SHIFT_VDP_SCHEMA = 'GET_SHIFT_VDP_SCHEMA';
export const SHIFT_CUSTOM_LINE_DATA = 'SHIFT_CUSTOM_LINE_DATA';

export const actions: ActionTree<ShiftCustomPlanFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(SHIFT_VDP_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    })
            ]).then((result: ServerResult) => {
                const _res = result.getOperation(SHIFT_VDP_DATA).getContent()
                commit('SET_FORM_DATA', {
                    document: _res.document,
                    schema: _res.schema,
                });

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(SHIFT_VDP_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:shift_custom_plan`
                    }),
            ]).then((result: ServerResult) => {
                const opLineResult = result.getOperation(SHIFT_CUSTOM_LINE_DATA);
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(SHIFT_VDP_SCHEMA).getContent(),
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = vm.$q('SHIFT_VDP_DATA_SAVE')
                    .update(documentId, normalizedForm)
            } else {
                saveQuery = vm.$q('SHIFT_VDP_DATA_SAVE')
                    .create('s:shift_custom_plan', normalizedForm)
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('SHIFT_VDP_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    addItem({ state }, {storage_id}) {
        state.storages.push({storage_id: storage_id})
    },

    removeItem({ state }, index: number) {
        if (state.storages[index].id) {
            state.targetForDelete.push(state.storages[index].id)
        }

        state.storages.splice(index, 1);
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    changeStorageValue({ state }, { index, field, newValue}) {
        const _sum = state.storages.reduce((prev: any, item: any, idx) => {
            return prev + (idx == index ? 0 : parseInt(item.quantity))
        }, 0)
        switch (field) {
            case 'quantity':
                newValue = parseInt(newValue)
                if (_sum + newValue > state.form.quantity) {
                    newValue = state.form.quantity - _sum;
                }
                state.storages[index][field] = newValue;
                break

            default:
                state.storages[index][field] = newValue;
        }
    },

    addAttachments({state, dispatch}, {newValue}) {
      state.form.attachments = [
        newValue,
        ...(state.form.attachments ? state.form.attachments : []),
      ]
    },

};

export const mutations: MutationTree<ShiftCustomPlanFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_SHIFT_VDP_DATA(state, {document}) {
        state.document = document;
    },

    SET_FORM_DATA(state, {document, schema, line_id}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
        state.lineId = line_id;
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: ShiftCustomPlanFormState, {field, newValue}) {
        const schema_field = state.schema.fields.filter((item: any) => {
            return item.code == field
        })[0]

        const calc = (form: any) => {
            console.log(form.weight_brutto, form.quantity, form.weight_palett, form.container_tare_weight)
            if (form.quantity && form.weight_brutto && form.weight_palett) {
                form.weight_neto = parseFloat(form.weight_brutto) - (parseFloat(form.quantity) * parseFloat(form.container_tare_weight)) - parseFloat(form.weight_palett);
            }
        }

        if (schema_field && (schema_field.type == 'decimal' || schema_field.type == 'integer')) {
            console.log('newValue', newValue)
            newValue = parseFloat(newValue.replace(',', '.'))
            console.log('newValue', newValue)
        }
        switch (field) {
            case 'quantity':
                if (newValue > 50) {
                    newValue = 50;
                }
                state.form[field] = newValue;
                calc(state.form)
                break

            case 'weight_palett':
                if (newValue > 30) {
                    newValue = 30;
                }
                state.form[field] = newValue;
                calc(state.form)
                break

            case 'weight_brutto':
                if (newValue > 500) {
                    newValue = 500;
                }
                state.form[field] = newValue;
                calc(state.form)
                break

            case 'plan_weight':
                state.form[field] = newValue;
                state.form.deviant_weight = numeral(parseFloat(state.form.plan_weight) - parseFloat(state.form.weight_neto)).format('0.00')
                break

            default:
                state.form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: ShiftCustomPlanFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_shift_custom_plan_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
